import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '../lib/api';
import { getUserId, isLoggedIn, login } from '../lib/liff';
import { applyThemeColors } from '../lib/store';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import { IconTicket } from '../components/Icons';
import { Screen, DateTile, StatusPill, EmptyState, SectionTitle, formatNtd, formatDate, btnPrimary } from '../components/ui';

interface Booking {
  id: string;
  eventId?: string;
  eventTitle: string;
  eventDate?: string;
  thumbnailImageUrl?: string;
  status: string;
  paymentStatus: string;
  totalAmount: number;
  createdAt: string;
}

export default function MyBookingsPage() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    applyThemeColors();
    const lineUserId = getUserId();
    if (!lineUserId) {
      if (!isLoggedIn()) { login(); return; }
      setLoading(false);
      return;
    }
    api.getUserBookings(lineUserId).then((response) => {
      if (response.success && response.data) setBookings(response.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <LoadingSpinner />;

  const active = bookings.filter((b) => b.status !== 'cancelled' && b.paymentStatus !== 'rejected');
  const pending = active.filter((b) => b.paymentStatus === 'pending');
  const confirmed = active.filter((b) => b.paymentStatus !== 'pending');
  const past = bookings.filter((b) => !active.includes(b));

  return (
    <Screen className="pb-28">
      <div className="safe-top px-5 pt-4 pb-2 flex flex-col leading-tight">
        <h1 className="text-xl font-black">我的報名</h1>
        <span className="text-[13px] text-muted tabular">
          {bookings.length === 0 ? '還沒有報名紀錄' : pending.length > 0 ? `${pending.length} 筆還沒匯款` : `共 ${bookings.length} 筆`}
        </span>
      </div>

      <div className="flex flex-col gap-6 px-5 pt-4">
        {bookings.length === 0 ? (
          <EmptyState
            icon={<IconTicket size={28} />}
            title="還沒有報名紀錄"
            hint="報名後，匯款資訊和確認狀態都會在這裡。"
            action={<Link to="/" className={btnPrimary}>看看開放報名的活動</Link>}
          />
        ) : (
          <>
            {pending.length > 0 && <Group title="待匯款" items={pending} tone="warn" />}
            {confirmed.length > 0 && <Group title="已確認" items={confirmed} tone="ok" />}
            {past.length > 0 && <Group title="已取消" items={past} tone="muted" />}
          </>
        )}
      </div>

      <BottomNav active="my" />
    </Screen>
  );
}

function Group({ title, items, tone }: { title: string; items: Booking[]; tone: 'warn' | 'ok' | 'muted' }) {
  return (
    <section className="flex flex-col gap-2.5">
      <SectionTitle>{title}</SectionTitle>
      {items.map((b) => (
        <Link
          key={b.id}
          to={`/booking/${b.id}`}
          className={clsx('flex items-center gap-3.5 px-4 py-3.5 rounded-card text-ink',
            tone === 'warn' ? 'bg-warn-soft' : tone === 'ok' ? 'bg-soft' : 'border-[1.5px] border-dashed border-line')}
        >
          {b.thumbnailImageUrl ? (
            <img src={b.thumbnailImageUrl} alt="" className="w-14 h-14 shrink-0 rounded-tile object-cover" />
          ) : (
            <DateTile date={b.eventDate} muted={tone === 'muted'} color="#1F2A37" />
          )}
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <span className="text-[15px] font-bold leading-snug line-clamp-2">{b.eventTitle}</span>
            <div className="flex items-center gap-2 text-xs text-muted tabular">
              <span>{formatDate(b.eventDate)}</span>
              <span>·</span>
              <span>{formatNtd(b.totalAmount)}</span>
            </div>
          </div>
          <StatusPill status={tone === 'muted' ? 'rejected' : b.paymentStatus} />
        </Link>
      ))}
    </section>
  );
}
